import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  ProgressBar,
  Divider,
  DescriptionList,
  DataTable,
} from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { IdentityPackingControl } from '../components/orders/IdentityPackingControl';

interface PackingListItem {
  identity_tracked?: number;
  id: number;
  packing_list_id: number;
  packing_list_order_id: number;
  order_item_id: number;
  product_id: number | null;
  sku: string;
  title: string;
  quantity_required: number;
  quantity_packed: number;
  bin_location: string | null;
  warehouse_location: string | null;
  status: 'pending' | 'packed' | 'short';
  packed_at: string | null;
  packed_by: string | null;
  barcode_verified: number;
}

interface PackingListOrder {
  id: number;
  packing_list_id: number;
  order_id: number;
  pack_sequence: number;
  status: string;
  packed_at: string | null;
  packed_by: string | null;
  notes: string | null;
  shopify_order_number: string;
  customer_name: string;
  customer_email: string;
  shipping_address: string;
  items: PackingListItem[];
}

interface PackingList {
  id: number;
  name: string;
  status: 'pending' | 'in_progress' | 'completed';
  warehouse_id: number | null;
  warehouse_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  total_orders: number;
  total_items: number;
  packed_items: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  created_by_name: string | null;
  orders: PackingListOrder[];
}

export default function PackingListDetail() {
  const { packingListId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, error } = useApi();
  const [packingList, setPackingList] = useState<PackingList | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadPackingList();
  }, [packingListId]);

  async function loadPackingList() {
    setLoading(true);
    const data = await fetchApi<{ success: boolean; packing_list: PackingList }>(
      `/warehouse/packing-lists/${packingListId}`
    );

    if (data?.success && data.packing_list) {
      setPackingList(data.packing_list);
    }
    setLoading(false);
  }

  const handleStartPacking = async () => {
    setActionLoading(true);
    await fetchApi(`/warehouse/packing-lists/${packingListId}/start`, {
      method: 'POST',
    });
    await loadPackingList();
    setActionLoading(false);
  };

  const handlePackItem = async (itemId: number, status: 'packed' | 'short') => {
    const item = packingList?.orders
      .flatMap(o => o.items)
      .find(i => i.id === itemId);

    if (!item) return;

    await fetchApi(`/warehouse/packing-lists/${packingListId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        quantity_packed: status === 'packed' ? item.quantity_required : 0,
        status,
      }),
    });
    await loadPackingList();
  };

  const handlePackAllOrder = async (packingOrderId: number) => {
    setActionLoading(true);
    await fetchApi(`/warehouse/packing-lists/${packingListId}/orders/${packingOrderId}/pack-all`, {
      method: 'POST',
    });
    await loadPackingList();
    setActionLoading(false);
  };

  const handleComplete = async () => {
    setActionLoading(true);
    const result = await fetchApi<{ success: boolean; error?: string }>(
      `/warehouse/packing-lists/${packingListId}/complete`,
      { method: 'POST' }
    );
    if (result?.success) {
      await loadPackingList();
    }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this packing list?')) return;

    setActionLoading(true);
    const result = await fetchApi<{ success: boolean }>(
      `/warehouse/packing-lists/${packingListId}`,
      { method: 'DELETE' }
    );
    if (result?.success) {
      navigate('/packing-lists');
    }
    setActionLoading(false);
  };

  const getStatusBadge = (status: string) => {
    const tones: Record<string, 'info' | 'success' | 'warning'> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      in_progress: 'In Progress',
      completed: 'Completed',
    };
    return <Badge tone={tones[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const getItemStatusBadge = (status: string) => {
    const tones: Record<string, 'info' | 'success' | 'critical'> = {
      pending: 'info',
      packed: 'success',
      short: 'critical',
    };
    return <Badge tone={tones[status] || 'info'}>{status}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProgress = () => {
    if (!packingList || packingList.total_items === 0) return 0;
    return Math.round((packingList.packed_items / packingList.total_items) * 100);
  };

  const parseShippingAddress = (addressJson: string | null) => {
    if (!addressJson) return null;
    try {
      return JSON.parse(addressJson);
    } catch {
      return null;
    }
  };

  if (loading || !packingList) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <Text as="p">Loading packing list details...</Text>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const isInProgress = packingList.status === 'in_progress';
  const isPending = packingList.status === 'pending';
  const isCompleted = packingList.status === 'completed';
  const allItemsPacked = packingList.orders.every(o =>
    o.items.every(i => i.status === 'packed' && i.quantity_packed === i.quantity_required)
  );

  return (
    <Page
      title={packingList.name}
      subtitle={`${packingList.total_orders} orders, ${packingList.total_items} items`}
      backAction={{ content: 'Packing Lists', onAction: () => navigate('/packing-lists') }}
      titleMetadata={getStatusBadge(packingList.status)}
      primaryAction={
        isPending
          ? { content: 'Start Packing', onAction: handleStartPacking, loading: actionLoading }
          : isInProgress && allItemsPacked
          ? { content: 'Complete', onAction: handleComplete, loading: actionLoading }
          : undefined
      }
      secondaryActions={
        isPending
          ? [{ content: 'Delete', destructive: true, onAction: handleDelete }]
          : undefined
      }
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Progress Summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Progress</Text>
                <Text as="span" variant="bodyMd">
                  {packingList.packed_items} / {packingList.total_items} items packed ({getProgress()}%)
                </Text>
              </InlineStack>
              <ProgressBar progress={getProgress()} tone="primary" />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Packing List Details */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Details</Text>
              <DescriptionList
                items={[
                  { term: 'Warehouse', description: packingList.warehouse_name || 'Not specified' },
                  { term: 'Assigned To', description: packingList.assigned_to_name || 'Unassigned' },
                  { term: 'Created', description: formatDate(packingList.created_at) },
                  { term: 'Created By', description: packingList.created_by_name || '-' },
                  ...(packingList.started_at ? [{ term: 'Started', description: formatDate(packingList.started_at) }] : []),
                  ...(packingList.completed_at ? [{ term: 'Completed', description: formatDate(packingList.completed_at) }] : []),
                ]}
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Orders */}
        {packingList.orders.map((order) => {
          const address = parseShippingAddress(order.shipping_address);
          const orderAllPacked = order.items.every(i => i.status === 'packed' && i.quantity_packed === i.quantity_required);
          const orderPendingItems = order.items.filter(i => i.status === 'pending').length;

          return (
            <Layout.Section key={order.id}>
              <Card>
                <BlockStack gap="400">
                  {/* Order Header */}
                  <InlineStack align="space-between" wrap={false}>
                    <BlockStack gap="100">
                      <InlineStack gap="200" align="start">
                        <Text as="span" variant="headingMd">
                          Order #{order.shopify_order_number}
                        </Text>
                        <Badge tone={orderAllPacked ? 'success' : 'info'}>
                          {orderAllPacked ? 'Packed' : `${orderPendingItems} pending`}
                        </Badge>
                      </InlineStack>
                      <Text as="span" tone="subdued">
                        {order.customer_name}
                        {address && ` - ${address.city || ''}, ${address.country || ''}`}
                      </Text>
                    </BlockStack>
                    {isInProgress && !orderAllPacked && !order.items.some(i=>i.identity_tracked&&i.quantity_packed<i.quantity_required) && (
                      <Button
                        onClick={() => handlePackAllOrder(order.id)}
                        disabled={actionLoading}
                      >
                        Pack All
                      </Button>
                    )}
                  </InlineStack>

                  <Divider />

                  {/* Items Table */}
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
                    headings={['Location', 'SKU', 'Product', 'Qty', 'Status', isInProgress ? 'Action' : '']}
                    rows={order.items.map((item) => [
                      <Text as="span" variant="bodyMd" fontWeight="bold">
                        {item.bin_location || item.warehouse_location || '-'}
                      </Text>,
                      item.sku || '-',
                      <BlockStack gap="100">
                        <Text as="span" variant="bodySm">{item.title}</Text>
                      </BlockStack>,
                      `${item.quantity_packed}/${item.quantity_required}`,
                      getItemStatusBadge(item.status),
                      item.identity_tracked ? <IdentityPackingControl listId={packingList.id} lineId={item.id} canPack={isInProgress} onSaved={()=>void loadPackingList()}/> : isInProgress && (item.status === 'pending' || item.status === 'short') ? (
                        <InlineStack gap="200">
                          <Button
                            size="slim"
                            tone="success"
                            onClick={() => handlePackItem(item.id, 'packed')}
                          >
                            Pack
                          </Button>
                          <Button
                            size="slim"
                            tone="critical"
                            onClick={() => handlePackItem(item.id, 'short')}
                          >
                            Short
                          </Button>
                        </InlineStack>
                      ) : item.status === 'packed' ? (
                        <Text as="span" tone="success">Packed</Text>
                      ) : item.status === 'short' ? (
                        <Text as="span" tone="critical">Short</Text>
                      ) : '-',
                    ])}
                  />

                  {/* Link to order */}
                  <InlineStack align="end">
                    <Button
                      size="slim"
                      url={`/orders/${order.order_id}`}
                      variant="plain"
                    >
                      View Order Details
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          );
        })}

        {/* Complete Button (bottom) */}
        {isInProgress && allItemsPacked && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300" inlineAlign="center">
                <Text as="p" variant="bodyMd">
                  All items have been packed. Complete this packing list to update order statuses.
                </Text>
                <Button
                  variant="primary"
                  tone="success"
                  onClick={handleComplete}
                  loading={actionLoading}
                >
                  Complete Packing List
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Completed Message */}
        {isCompleted && (
          <Layout.Section>
            <Banner tone="success">
              This packing list has been completed. All {packingList.total_orders} orders have been marked as 'packed'.
            </Banner>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
